// Board do kanban de deals (Story 11.5 / AC4/AC5/AC6).
// Orquestra DndContext (@dnd-kit): sensors pointer + keyboard (acessível, AC4),
// DragOverlay (card semitransparente, AC4), e o move otimista com rollback (AC5).
// Mover pra fechamento_perdido abre LostDealDialog pedindo motivo antes do PUT (AC6).
// RBAC: sem sensors quando read-only → drag desabilitado (AC8).

import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { toast } from 'sonner';

import type { CrmDealStageSchema } from '@control-center/shared';

import { LostDealDialog } from '../LostDealDialog';
import { DealCard } from './DealCard';
import { KanbanColumn } from './KanbanColumn';
import {
  LOST_STAGE,
  OPEN_STAGES,
  TERMINAL_STAGES,
  daysInStage,
  moveStageErrorMessage,
} from '../../../lib/deal-helpers';
import { extractErrorMessage } from '../../../lib/errors';
import { useMoveCrmDealStage, type CrmDealsKanbanFilters } from '../../../hooks/use-crm-deals';
import type { CrmDealEntity, CrmDealKanban } from '../../../lib/crm-deals-api';

export interface KanbanBoardProps {
  kanban: CrmDealKanban;
  /** Filtros ativos — passados ao move pra mirar a queryKey no rollback (AC5). */
  filters: CrmDealsKanbanFilters;
  /** Mapa id→nome de responsável (resolve nome no card flat — AC3). */
  usersById: Map<string, string>;
  /** RBAC: false desabilita o drag (read-only — AC8). */
  canDrag: boolean;
  /** Limiar de "parado há +X dias" pra destacar cards (AC7). undefined = off. */
  stalledDays?: number;
  onOpenDeal: (deal: CrmDealEntity) => void;
}

/** Deal pendente de confirmação de perda (aguardando motivo no modal — AC6). */
interface PendingLost {
  deal: CrmDealEntity;
}

export function KanbanBoard({
  kanban,
  filters,
  usersById,
  canDrag,
  stalledDays,
  onOpenDeal,
}: KanbanBoardProps): JSX.Element {
  const move = useMoveCrmDealStage(filters);

  const [activeDeal, setActiveDeal] = useState<CrmDealEntity | null>(null);
  const [pendingLost, setPendingLost] = useState<PendingLost | null>(null);
  // Ids em movimento (PUT pendente) — opacidade reduzida no card (AC9).
  const [movingIds, setMovingIds] = useState<Set<string>>(new Set());

  // Sensors só quando pode arrastar (RBAC — AC8). Keyboard sensor = acessível (AC4).
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 4 },
  });
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const enabledSensors = useSensors(pointerSensor, keyboardSensor);
  const noSensors = useSensors();
  const sensors = canDrag ? enabledSensors : noSensors;

  const stalledThreshold = stalledDays;

  /** Resolve o deal arrastado a partir do id ativo. */
  const dealById = useMemo(() => {
    const map = new Map<string, CrmDealEntity>();
    for (const stage of OPEN_STAGES) {
      for (const deal of kanban[stage]) map.set(deal.id, deal);
    }
    return map;
  }, [kanban]);

  const handleDragStart = (event: DragStartEvent): void => {
    const deal = dealById.get(String(event.active.id));
    setActiveDeal(deal ?? null);
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    setActiveDeal(null);
    const { active, over } = event;
    if (!over) return;

    const deal = dealById.get(String(active.id));
    if (!deal) return;

    // Destino: coluna (droppable id = stage) ou outro card (data.stage).
    const overStage =
      (over.data.current?.stage as CrmDealStageSchema | undefined) ??
      (over.id as CrmDealStageSchema);
    if (!overStage || overStage === deal.stage) return;

    // Mover pra fechamento_perdido exige motivo → abre modal antes do PUT (AC6).
    if (overStage === LOST_STAGE) {
      setPendingLost({ deal });
      return;
    }

    runMove(deal, overStage);
  };

  const runMove = (deal: CrmDealEntity, toStage: CrmDealStageSchema, reason?: string): void => {
    setMovingIds((prev) => new Set(prev).add(deal.id));
    move.mutate(
      { deal, toStage, reason },
      {
        onSuccess: () => {
          if (toStage === 'fechamento_ganho') {
            toast.success('Deal marcado como ganho.', { description: deal.title });
          } else if (toStage === LOST_STAGE) {
            toast.success('Deal marcado como perdido.', { description: deal.title });
          }
        },
        onError: (err) => {
          const code = errorCode(err);
          toast.error('Não conseguimos mover o deal.', {
            description: moveStageErrorMessage(
              code,
              extractErrorMessage(err, 'Tente de novo em instantes.'),
            ),
          });
        },
        onSettled: () => {
          setMovingIds((prev) => {
            const next = new Set(prev);
            next.delete(deal.id);
            return next;
          });
        },
      },
    );
  };

  const confirmLost = (reason: string): void => {
    if (!pendingLost) return;
    runMove(pendingLost.deal, LOST_STAGE, reason);
    setPendingLost(null);
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveDeal(null)}
      >
        <div className="flex gap-3 overflow-x-auto pb-2">
          {OPEN_STAGES.map((stage) => (
            <KanbanColumn key={stage} stage={stage} deals={kanban[stage]}>
              {kanban[stage].map((deal) => (
                <DealCard
                  key={deal.id}
                  deal={deal}
                  responsibleName={
                    deal.responsible_user_id ? usersById.get(deal.responsible_user_id) : undefined
                  }
                  disabled={!canDrag}
                  isMoving={movingIds.has(deal.id)}
                  isStalled={isStalled(deal, stalledThreshold)}
                  onOpen={onOpenDeal}
                />
              ))}
            </KanbanColumn>
          ))}

          {TERMINAL_STAGES.map((stage) => (
            <KanbanColumn
              key={stage}
              stage={stage}
              deals={[]}
              terminal
              tone={stage === 'fechamento_ganho' ? 'good' : 'bad'}
            />
          ))}
        </div>

        <DragOverlay>
          {activeDeal ? (
            <DealCard
              deal={activeDeal}
              responsibleName={
                activeDeal.responsible_user_id
                  ? usersById.get(activeDeal.responsible_user_id)
                  : undefined
              }
              onOpen={() => undefined}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {pendingLost && (
        <LostDealDialog
          open={!!pendingLost}
          onOpenChange={(open) => {
            // Cancelar = rollback (o card nunca saiu da coluna no cache — AC6).
            if (!open) setPendingLost(null);
          }}
          dealTitle={pendingLost.deal.title}
          isPending={move.isPending}
          onConfirm={confirmLost}
        />
      )}
    </>
  );
}

/** "Parado há +X dias": stageEnteredAt antigo o bastante (AC7). */
function isStalled(deal: CrmDealEntity, thresholdDays?: number): boolean {
  if (thresholdDays === undefined || deal.status !== 'open') return false;
  return daysInStage(deal.stage_entered_at) >= thresholdDays;
}

/** Extrai o `code` da resposta de erro do Axios (sem acoplar ao formato). */
function errorCode(err: unknown): string | undefined {
  if (
    typeof err === 'object' &&
    err !== null &&
    'response' in err &&
    typeof (err as { response?: unknown }).response === 'object'
  ) {
    const response = (err as { response?: { data?: { error?: { code?: unknown } } } }).response;
    const code = response?.data?.error?.code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}
